import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { ACTIVATE_APP_CSS, CRITICAL_CSS } from "../lib/critical-css";
import { RECOVER_STALE_CLIENT } from "../lib/stale-client-recovery";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { captureLeadSource, rememberPage } from "../lib/lead-context";
import { initDelegatedGoals } from "../lib/analytics";

import { NotFoundView } from "../components/site/NotFoundView";
import { CookieConsent } from "../components/site/CookieConsent";

import { SITE_URL } from "../lib/site";

const notFoundSlogans: [string, string][] = [
  ["Страница ушла в продакшен", "раньше срока"],
  ["Потерялась", "между билдами"],
  ["404. Но наши кейсы", "на месте"],
];

const tickerWords = [
  "ошибка 404",
  "страница не найдена",
  "но мы уже чиним",
  "а пока — наши кейсы",
  "hay.agency",
];

const LazyCursorTrail = lazy(() =>
  import("../components/site/CursorTrail").then((module) => ({
    default: module.CursorTrail,
  })),
);

function NotFoundComponent() {
  return (
    <NotFoundView
      slogans={notFoundSlogans}
      ticker={tickerWords}
      description="Такой страницы нет или она переехала. Проверьте адрес — или вернитесь туда, где всё работает."
    />
  );
}


function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      {
        name: "google-site-verification",
        content: "UrPCtJs69EExBvanEkc333v5DQKSkEOzUKdSbSYJKN4",
      },
      { title: "HAY.agency" },
      { name: "description", content: "Маркетинговое агентство HAY.agency" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "HAY.agency" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      {
        id: "app-css",
        rel: "stylesheet",
        href: appCss,
        media: "print",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "/fonts/manrope-cyrillic.woff2",
        crossOrigin: "anonymous",
      },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg", sizes: "any" },
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "dns-prefetch", href: "https://mc.yandex.ru" },
    ],
    styles: [{ id: "critical-css", children: CRITICAL_CSS }],
    scripts: [
      {
        type: "text/javascript",
        children: RECOVER_STALE_CLIENT,
      },
      {
        type: "text/javascript",
        children: ACTIVATE_APP_CSS,
      },
      {
        type: "text/javascript",
        // Метрика инициализируется после загрузки страницы / первого
        // взаимодействия, чтобы не занимать main thread при первой отрисовке.
        children:
          "(function(){var w=window,d=document;w.ym=w.ym||function(){(w.ym.a=w.ym.a||[]).push(arguments)};w.ym.l=1*new Date();ym(5204773,'init',{trackHash:true,clickmap:true,referrer:d.referrer,url:location.href,accurateTrackBounce:true,trackLinks:true});var loaded=false;function load(){if(loaded)return;loaded=true;var s=d.createElement('script');s.async=1;s.src='https://mc.yandex.ru/metrika/tag.js';d.head.appendChild(s);}var evts=['scroll','pointerdown','keydown','touchstart'];function once(){evts.forEach(function(e){w.removeEventListener(e,once)});load();}evts.forEach(function(e){w.addEventListener(e,once,{passive:true,once:true})})})();",
      },

      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "@id": `${SITE_URL}/#organization`,
          name: "hay.agency",
          url: SITE_URL,
          description:
            "Маркетинговое агентство HAY.agency. Разработка сайтов и цифровых продуктов, SEO, реклама, геосервисы, CRM и автоматизация с использованием ИИ-инструментов.",

          email: "info@hay.agency",
          telephone: "+7 (495) 132-22-56",
          address: {
            "@type": "PostalAddress",
            streetAddress: "ул. Правды 8к13",
            addressCountry: "RU",
          },
          sameAs: ["https://t.me/hayagency"],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          name: "hay.agency",
          publisher: { "@id": `${SITE_URL}/#organization` },
          url: SITE_URL,
          inLanguage: "ru",
        }),
      },
    ],
  }),

  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="ru">
      <head>
        <HeadContent />
        <noscript>
          <link rel="stylesheet" href={appCss} />
        </noscript>
      </head>
      <body>
        <noscript>
          <div>
            <img
              src="https://mc.yandex.ru/watch/5204773"
              loading="lazy"
              style={{ position: "absolute", left: "-9999px" }}
              alt="" decoding="async" />
          </div>
        </noscript>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

/** First-touch источник, история страниц и делегированные цели Метрики. */
function TrackingEffects() {
  const href = useRouterState({ select: (s) => s.location.href });

  useEffect(() => {
    captureLeadSource();
    return initDelegatedGoals();
  }, []);

  useEffect(() => {
    rememberPage(typeof window === "undefined" ? href : window.location.href);
  }, [href]);

  return null;
}

/** Загружает декоративный canvas только на desktop после первого движения мыши. */
function DeferredCursorTrail() {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const enable = () => setEnabled(true);
    window.addEventListener("pointermove", enable, { passive: true, once: true });
    return () => window.removeEventListener("pointermove", enable);
  }, []);

  if (!enabled) return null;
  return (
    <Suspense fallback={null}>
      <LazyCursorTrail />
    </Suspense>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      {/* Required: nested routes render here. Removing <Outlet /> breaks all child routes. */}
      <Outlet />
      <TrackingEffects />
      <DeferredCursorTrail />
      <CookieConsent />
    </QueryClientProvider>
  );
}
